import { useEffect, useMemo, useState } from "react";
import { useRoute, useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { customFetch, customerCustomFetch } from "@workspace/api-client-react";
import type { CatalogResponse } from "@workspace/api-client-react";
import { ChevronLeft, CheckCircle2, Loader2, User, UtensilsCrossed } from "lucide-react";
import { getCartStore } from "@/hooks/use-cart";
import { useCustomerAuth } from "@/hooks/use-customer-auth";
import { useStoreColors } from "@/hooks/use-store-colors";
import { subscribeCustomerPush } from "@/hooks/use-push-notifications";
import { setStoreTimezone } from "@/lib/store-timezone";
import { formatCurrency, orderDisplayCode } from "@/lib/utils";
import { extractApiErrorMessage } from "@/lib/api-error";
import { lerMesa, sairDaMesa, nomeDaMesa, NomeDaMesa } from "@/lib/mesa";
import { Button } from "@/components/ui/button";
import { BrandTile } from "@/components/BrandTile";
import { ClosedStoreCard } from "@/components/ClosedStoreCard";
import { StoreInactiveCard } from "@/components/StoreInactiveCard";
import { CustomerLoginModal } from "@/components/CustomerLoginModal";
import { useToast } from "@/hooks/use-toast";

/**
 * Pedido na mesa (QR Code, Ultra): o "finalizar" da mesa. Sem entrega, sem
 * endereço e sem pagamento — o pedido vai pra cozinha e entra na conta da mesa,
 * que a loja fecha no PDV. Quem pede ainda se identifica pelo WhatsApp, como
 * no link: é o mesmo cadastro de cliente (pontos, histórico).
 */
export default function TableCheckout() {
  const [, params] = useRoute("/loja/:slug/mesa");
  const slug = params?.slug || "";
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const mesa = useMemo(() => (slug ? lerMesa(slug) : null), [slug]);

  const { data: catalog } = useQuery<CatalogResponse>({
    queryKey: ["catalog", slug, null],
    queryFn: () => customFetch(`/api/catalog/${slug}`),
    enabled: !!slug,
  });
  const store = catalog?.store;
  // Comanda individual («1 QR code para cada cliente»): os textos dizem comanda, não mesa.
  const porCliente = (store as { tableQrPerClient?: boolean } | undefined)?.tableQrPerClient === true;
  const nome = nomeDaMesa(porCliente);
  useStoreColors(store);
  useEffect(() => {
    setStoreTimezone((store as { timezone?: string | null } | undefined)?.timezone ?? null);
  }, [store]);

  const useCart = getCartStore(slug);
  const items = useCart((s) => s.items);
  const total = useCart((s) => s.getTotal());
  const clearCart = useCart((s) => s.clearCart);

  const { customer, isLoading: customerLoading } = useCustomerAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState<{ codigo: string; mesa: string } | null>(null);

  const lojaDesativada = (catalog as { isActive?: boolean } | undefined)?.isActive === false;
  const closedAndBlocked =
    catalog?.isOpen === false &&
    (store as { acceptOrdersWhenClosed?: boolean } | undefined)?.acceptOrdersWhenClosed === false;
  const encomendas = items.filter((i) => (i.product as { isOrderBased?: boolean }).isOrderBased);

  const enviar = async () => {
    if (!mesa) return;
    if (encomendas.length > 0) {
      toast({ title: `${encomendas[0].product.name} é encomenda: não sai no pedido da ${nome}.`, variant: "destructive" });
      return;
    }
    setEnviando(true);
    try {
      const order = await customerCustomFetch<{ id: string; dailyOrderNumber: number | null }>(`/api/catalog/${slug}/orders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({
            productId: i.product.id,
            quantity: i.quantity,
            notes: i.notes,
            ...(i.selectedOptions ? { selectedOptions: i.selectedOptions } : {}),
            ...(i.removedIngredients ? { removedIngredients: i.removedIngredients } : {}),
            ...(i.weightKg != null ? { weightKg: i.weightKg } : {}),
          })),
          paymentMethod: "Pendente",
          deliveryMode: "retirada",
          notes: notes.trim() || null,
          tableNumber: mesa.mesa,
          tableToken: mesa.token,
        }),
      });
      clearCart();
      setEnviado({ codigo: orderDisplayCode(order, (store as { orderCodeStyle?: string | null } | undefined)?.orderCodeStyle), mesa: mesa.mesa });
      void subscribeCustomerPush(store?.id);
    } catch (e: any) {
      if (e?.data?.error?.code === "MESA_INVALIDA") sairDaMesa(slug);
      toast({ title: extractApiErrorMessage(e, "Não foi possível enviar o pedido. Tente de novo."), variant: "destructive" });
    } finally {
      setEnviando(false);
    }
  };

  if (!store) {
    return (
      <div className="min-h-[100svh] flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (enviado) {
    return (
      <div className="min-h-[100svh] bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-card max-w-md w-full p-8 rounded-3xl shadow-xl border border-border/50 text-center space-y-4" data-testid="mesa-pedido-enviado">
          <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto" />
          <div>
            <h2 className="text-2xl font-bold font-display">Pedido enviado!</h2>
            <p className="text-muted-foreground mt-1">
              {NomeDaMesa(porCliente)} {enviado.mesa} · Pedido #{enviado.codigo}
            </p>
          </div>
          <p className="text-sm text-muted-foreground">A cozinha já recebeu. Você paga no final, na conta da {nome}.</p>
          <Button className="w-full h-12" onClick={() => setLocation(`/loja/${slug}`)} data-testid="mesa-pedir-mais">
            Fazer outro pedido
          </Button>
        </div>
      </div>
    );
  }

  if (!mesa) {
    return (
      <div className="min-h-[100svh] bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-card max-w-md w-full p-8 rounded-3xl shadow-xl border border-border/50 text-center space-y-4">
          <UtensilsCrossed className="w-12 h-12 text-muted-foreground mx-auto" />
          <h2 className="text-xl font-bold font-display">Leia o QR code da {nome} de novo</h2>
          <p className="text-sm text-muted-foreground">O pedido {porCliente ? "pela comanda" : "na mesa"} vale para quem leu o QR code. Se não estiver na loja, faça o pedido pelo cardápio.</p>
          <Button asChild className="w-full h-12">
            <Link href={`/loja/${slug}`}>Ver o cardápio</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-[100svh] bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-card max-w-md w-full p-8 rounded-3xl shadow-xl border border-border/50 text-center space-y-4">
          <h2 className="text-xl font-bold font-display">Seu carrinho está vazio</h2>
          <Button asChild className="w-full h-12">
            <Link href={`/loja/${slug}`}>Ver o cardápio</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (customerLoading) {
    return (
      <div className="min-h-[100svh] flex items-center justify-center bg-gray-100">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!customer) {
    return (
      <>
        <div className="min-h-[100svh] bg-gray-100 flex items-center justify-center p-4">
          <div className="bg-card max-w-md w-full p-8 rounded-3xl shadow-xl border border-border/50 text-center space-y-4">
            <BrandTile size="lg" className="mx-auto" />
            <div>
              <h2 className="text-2xl font-bold font-display">Para quem é o pedido?</h2>
              <p className="text-muted-foreground mt-2">Confirme seu WhatsApp para mandar o pedido da {nome} {mesa.mesa}.</p>
            </div>
            <Button className="w-full h-12" onClick={() => setLoginOpen(true)}>
              Entrar com WhatsApp
            </Button>
          </div>
        </div>
        <CustomerLoginModal
          open={loginOpen}
          onOpenChange={setLoginOpen}
          dismissible={false}
          askBirthDate={(store as any)?.askBirthDate}
          birthDateRequired={(store as any)?.birthDateRequired}
          storeSlug={slug}
          loyaltyEnabled={(store as any)?.loyaltyEnabled === true}
        />
      </>
    );
  }

  return (
    <div className="min-h-[100svh] bg-gray-100 max-w-3xl mx-auto flex flex-col relative">
      <header className="bg-card px-4 py-4 flex items-center gap-4 sticky top-0 z-10 shadow-sm">
        <Link href={`/loja/${slug}`} className="p-2 -ml-2 rounded-full hover:bg-muted text-muted-foreground">
          <ChevronLeft className="w-6 h-6" />
        </Link>
        <h1 className="text-xl font-bold font-display">Pedido da {nome} {mesa.mesa}</h1>
      </header>

      <div className="p-4 space-y-4 flex-1 pb-48">
        <div className="bg-card p-4 rounded-2xl shadow border border-border/50 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <User className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <p className="font-bold truncate">{customer.firstName}</p>
            <p className="text-sm text-muted-foreground truncate">{customer.whatsappFormattedNumber || `+${customer.whatsappNumber}`}</p>
          </div>
        </div>

        <div className="bg-card p-5 rounded-2xl shadow border border-border/50 space-y-3">
          <h2 className="font-bold text-lg">Seu pedido</h2>
          <ul className="divide-y divide-border/60">
            {items.map((i) => (
              <li key={i.cartItemId} className="py-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium">
                    {i.quantity}x {i.product.name}
                  </p>
                  {i.selectedOptions && i.selectedOptions.length > 0 && (
                    <p className="text-xs text-muted-foreground">{i.selectedOptions.map((o) => o.optionName).join(", ")}</p>
                  )}
                  {i.removedIngredients && i.removedIngredients.length > 0 && (
                    <p className="text-xs text-muted-foreground">Sem: {i.removedIngredients.join(", ")}</p>
                  )}
                  {i.notes && <p className="text-xs text-muted-foreground">Obs: {i.notes}</p>}
                </div>
                <span className="text-sm font-semibold shrink-0">{formatCurrency((i.unitPrice + i.optionsTotal) * i.quantity)}</span>
              </li>
            ))}
          </ul>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Alguma observação para a cozinha?"
            className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            data-testid="mesa-observacao"
          />
        </div>

        {lojaDesativada ? (
          <StoreInactiveCard />
        ) : !catalog?.isOpen ? (
          <ClosedStoreCard businessHours={(store?.businessHours ?? null) as Parameters<typeof ClosedStoreCard>[0]["businessHours"]} type="pedido" blocked={closedAndBlocked} />
        ) : null}
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-card border-t border-border/50 p-4 max-w-3xl mx-auto shadow-[0_-10px_40px_rgba(0,0,0,0.05)] z-20">
        <div className="flex justify-between items-end mb-1">
          <p className="text-sm font-medium text-muted-foreground">Total deste pedido</p>
          <p className="text-2xl font-bold font-display text-primary" data-testid="mesa-total">{formatCurrency(total)}</p>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Você paga no final, na conta da {nome}.</p>
        <Button
          className="w-full h-14 rounded-xl text-lg font-bold shadow-lg shadow-primary/25"
          disabled={enviando || closedAndBlocked || lojaDesativada}
          onClick={enviar}
          data-testid="mesa-enviar"
        >
          {enviando ? <Loader2 className="w-6 h-6 animate-spin" /> : lojaDesativada ? "Loja não está recebendo pedidos" : closedAndBlocked ? "Loja fechada" : "Enviar pedido para a cozinha"}
        </Button>
      </div>
    </div>
  );
}
